/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable no-alert */
/* eslint-disable no-restricted-globals */

// region Imports - External libraries
import React, { useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import { ApolloClient, ApolloLink, ApolloProvider, ErrorPolicy, InMemoryCache, split } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { atom } from "jotai";
// endregion Imports - External libraries

import { useAuth } from "@hooks/useAuth";

import IGraphQLExtensionError from "@store/interfaces/IGraphQLExtensionError";

/** Atoms */
import ExpiredSession from "@atoms/ExpiredSession";
import utils from "@helpers/utils";
import useTranslation from "@languages/useTranslation";
import { ECommandResultStatus } from "./helpers/types.graphql";

export enum EGraphQLClients {
  TECH = "tech",
  IDENTITY = "identity"
}

const baseUrl = "https://api.topconsuite.io";

export const moduleClients = {
  [EGraphQLClients.IDENTITY]: {
    moduleId: "49bfc198-e451-49a9-92cb-1947ed8b7993",
    productionRoute: `${baseUrl}/identity/`,
    qaRoute: `${baseUrl}/identity-qa/`,
    homologationRoute: `${baseUrl}/identity-hml/`,
    devRoute: `${baseUrl}/identity-qa/`
  }
};
export const currentClientAtom = atom<EGraphQLClients>(EGraphQLClients.IDENTITY);

const TelluriaApolloProvider: React.FC = ({ children }) => {

  const route = "graphql";
  const { addToast } = useToast();
  const { i18n } = useTranslation();

  const { getToken, getDecodedToken } = useAuth();

  const customFetch = React.useCallback(async (uri, options) => {

    const token = await getToken();

    // eslint-disable-next-line no-param-reassign
    options.headers = {
      ...options.headers,
      authorization: `Bearer ${token}`,
      "Accept-Language": i18n.language
    };

    if (window.location.hostname === "dev.topconsuite.app.br") {
      const decodedToken = await getDecodedToken();

      // eslint-disable-next-line no-param-reassign
      options.headers["X-TenantId"] = decodedToken?.extension_TenantId ?? "a8246ab8-7ebc-4792-969e-d56f5678066c";
    }

    return fetch(uri, options);
  }, [getToken, i18n.language]);

  const errorLink = onError(({ graphQLErrors, networkError }) => {

    if (networkError) addToast({ type: "error", title: "Connection not established", overwrite: true });
    if (graphQLErrors) {

      // Add all errors in a single string
      graphQLErrors.forEach(({ message, extensions }) => {

        // Normalize type of extensions with Default return execution error of GraphQL
        const error = extensions as IGraphQLExtensionError;

        error.data = {
          status: error?.data?.status ?? ECommandResultStatus.ERROR,
          errorCode: error?.data?.errorCode ?? "9999"
        };

        // If token expired, force user to login again
        if (error.code === "authorization") {

          ReactDOM.render(
            <ExpiredSession />,
            document.getElementById("extra")
          );

          return;
        }

        addToast({
          type: error.data.status === ECommandResultStatus.ERROR ? "error" : "info",
          title: error.data.errorCode,
          description: message.replace(/\\n/g, "<br />"),
          overwrite: false
        });
      });
    }
  });

  const baseOptions = { errorPolicy: "all" as ErrorPolicy };

  const httpLink = createUploadLink({
    fetch: customFetch,
    uri: (utils.getBackendBaseUrl() + route)
  });

  const link = ApolloLink.from([
    errorLink,
    httpLink
  ]);

  // Create the apollo client
  const apolloClient = useMemo(
    () => new ApolloClient({
      link,
      cache: new InMemoryCache(),
      defaultOptions: { mutate: baseOptions, query: baseOptions, watchQuery: baseOptions }
    }),
    []
  );

  // When change language, update the headers
  useEffect(() => {

    apolloClient.setLink(link);

    return () => {
      apolloClient.stop();
    };

  }, [i18n.language]);

  return (
    <ApolloProvider client={apolloClient}>
      {children}
    </ApolloProvider>
  );
};

export default TelluriaApolloProvider;
